import * as React from 'react';
import { useExperiment } from '@contexts/experiment/useExperiment';
import { universalLoginExperiment } from '@experiments/universalLogin';
import { SigninNavigationContext } from './SigninNavigationContext';
import { createSigninNavigationStore } from './createSigninNavigationStore';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { ULPath } from '@ul/common';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { useServiceResolver } from '@services/useServiceResolver';
import { useIsYCS, useIsYCSAppAgoda } from '@contexts/app/hooks/useIsYCS';
import { useIsAgoda } from '@contexts/app/hooks/useIsAgoda';
import { useIsJTB } from '@contexts/app/hooks/useIsJTB';
import { WhitelabelId } from '@whitelabel/WhitelabelId';

export function WithSigninNavigation<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithSigninNavigation: React.FC<React.PropsWithRef<TProps>> = props => {
        const experiments = useExperiment(universalLoginExperiment);
        const { feature } = useAppSetting();
        const { logger, measurement } = useServiceResolver();
        const isYcs = useIsYCS();
        const { initialPath, token, appId, whitelabelId } = useAppContext();
        const isMagicLinkLanding = isYcs && !!token;
        const isJTB = useIsJTB();

        const isEnabledByExperiment =
            experiments.LOGIN_REDESIGN ||
            experiments.LOGIN_REDESIGN_DICTATOR ||
            experiments.LOGIN_REDESIGN_YCS_APP ||
            (experiments.LOGIN_REDESIGN_YCS && !isMagicLinkLanding) ||
            experiments.LOGIN_REDESIGN_WEBSPA;

        const paths: ULPath[] = ['signup', 'signupEmail', 'signupPhone', 'signin', 'deleteAccount'];
        const walletPaths: ULPath[] = ['walletAuth', 'walletResetPin', 'walletSca'];

        const isYcsAgoda = isYcs && useIsAgoda();
        const isYcsAppAgoda = useIsYCSAppAgoda();

        const WL_APP_UNDER_EXPERIMENTS: Record<number, string[]> = {
            [WhitelabelId.AGODA]: ['ycs', 'ycsapp'],
            [WhitelabelId.AGODAUAT]: ['ycs', 'ycsapp'],
        };

        const isUnderExperiment =
            whitelabelId !== undefined && WL_APP_UNDER_EXPERIMENTS[whitelabelId]?.includes(appId?.toLowerCase() || '');

        const shouldEnableByExperiment = isUnderExperiment ? isEnabledByExperiment : true;

        const useNewLoginDesign =
            (paths.concat(walletPaths).includes(initialPath) || isYcsAgoda || isYcsAppAgoda) &&
            shouldEnableByExperiment &&
            !!feature.signInWithOtp;

        const [signInNavigationStore] = React.useState(() => createSigninNavigationStore(useNewLoginDesign));

        const tags = {
            appId: appId,
            isMagicLinkLanding: `${isMagicLinkLanding}`,
            isYcsExperimentOn: `${experiments.LOGIN_REDESIGN_YCS}`,
            pathIncluded: `${paths.includes(initialPath)}`,
            isSignInWithOtpEnabled: `${!!feature.signInWithOtp}`,
            isJTB: `${isJTB}`,
        };
        logger.info('SigninNavigationContext', undefined, { ...tags });
        measurement.count('ul.redesign.load', 1, { ...tags });

        return (
            <SigninNavigationContext.Provider value={{ signInNavigationStore }}>
                <MemoizedComponent {...props} />
            </SigninNavigationContext.Provider>
        );
    };

    ComponentWithSigninNavigation.displayName = `${Component.displayName || 'Component'}WithSigninNavigation`;

    return ComponentWithSigninNavigation;
}
